/**
 * UserManagement Component
 * 사용자 관리 UI
 */
import React, { useState } from 'react';
import {
  UserIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useUserManagement } from '../use-cases/useUserManagement';
import { userManagementService } from '../services/UserManagementService';
import { studentWithParentManagementRepository } from '../repositories/StudentWithParentManagementRepository';

const UserManagement: React.FC = () => {
  const {
    users,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    selectedUser,
    showUserModal,
    showCreateModal,
    newUser,
    setNewUser,
    filteredUsers,
    handleViewUser,
    openCreateModal,
    handleCreateUser,
    handleToggleActive,
    closeUserModal,
    closeCreateModal,
  } = useUserManagement();

  // 학부모 초대 모달 상태
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [parentPhone, setParentPhone] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [copied, setCopied] = useState(false);

  // 초대 모달 열기
  const openInviteModal = (student: any) => {
    setSelectedStudent(student);
    setParentPhone('');
    setInviteUrl('');
    setInviteError('');
    setCopied(false);
    setShowInviteModal(true);
  };

  // 초대 모달 닫기
  const closeInviteModal = () => {
    setShowInviteModal(false);
    setSelectedStudent(null);
    setParentPhone('');
    setInviteUrl('');
    setInviteError('');
    setCopied(false);
  };

  // 초대 링크 생성
  const handleCreateInvite = async () => {
    if (!selectedStudent || !parentPhone.trim()) {
      setInviteError('전화번호를 입력해주세요');
      return;
    }

    setInviteLoading(true);
    setInviteError('');

    try {
      const result =
        await studentWithParentManagementRepository.createParentInvite(
          selectedStudent.id,
          {
            phone: parentPhone.trim(),
            relationship: '부모',
          }
        );

      setInviteUrl(result.inviteUrl);
    } catch (err: any) {
      setInviteError(err.message || '초대 링크 생성에 실패했습니다');
    } finally {
      setInviteLoading(false);
    }
  };

  // URL 복사
  const handleCopyUrl = async () => {
    if (!inviteUrl) return;

    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('복사에 실패했습니다. URL을 직접 복사해주세요.');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600">사용자 데이터 로딩 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong className="font-bold">오류!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <UserIcon className="h-8 w-8 text-blue-600" />
            사용자 관리
          </h1>
          <p className="text-gray-600 mt-1">
            총 {users.length}명 · 활성 {users.filter((u) => u.is_active).length}
            명 · 비활성 {users.filter((u) => !u.is_active).length}명
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />새 사용자
        </button>
      </div>

      {/* 검색 */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="이름, 전화번호, 이메일로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* 사용자 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            사용자 목록 ({filteredUsers.length}명)
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사용자
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  연락처
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  가입일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  선호좌석
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  작업
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.user_number}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 flex items-center gap-1">
                      <PhoneIcon className="h-4 w-4 text-gray-400" />
                      {userManagementService.formatPhoneNumber(user.phone)}
                    </div>
                    {user.email && (
                      <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                        {user.email}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${userManagementService.getUserStatusColor(
                        user.is_active
                      )}`}
                    >
                      {userManagementService.getUserStatusLabel(user.is_active)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {userManagementService.formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.preferred_seats && user.preferred_seats.length > 0 ? (
                        user.preferred_seats.slice(0, 3).map((seat) => (
                          <span
                            key={seat}
                            className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                          >
                            {seat}번
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400">없음</span>
                      )}
                      {user.preferred_seats && user.preferred_seats.length > 3 && (
                        <span className="text-xs text-gray-500">
                          외 {user.preferred_seats.length - 3}개
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center gap-2">
                      <button
                        onClick={() => handleViewUser(user)}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        title="상세보기"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openInviteModal(user)}
                        className="text-purple-600 hover:text-purple-900 text-sm font-medium flex items-center gap-1"
                        title="부모님 초대"
                      >
                        <UserGroupIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(user.id, user.is_active)}
                        className={`text-sm font-medium ${
                          user.is_active
                            ? 'text-red-600 hover:text-red-900'
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={user.is_active ? '비활성화' : '활성화'}
                      >
                        {user.is_active ? '비활성화' : '활성화'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-8">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">사용자 없음</h3>
            <p className="mt-1 text-sm text-gray-500">
              검색 조건에 맞는 사용자가 없습니다.
            </p>
          </div>
        )}
      </div>

      {/* 새 사용자 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 text-center">
                새 사용자 등록
              </h3>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    이름 *
                  </label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="홍길동"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    전화번호 *
                  </label>
                  <input
                    type="tel"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="010-1234-5678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    이메일
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email@example.com"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={closeCreateModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  취소
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={!newUser.name || !newUser.phone}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  등록
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 사용자 상세 모달 */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 text-center">
                사용자 상세 정보
              </h3>
              <div className="mt-4 space-y-3">
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                    <UserIcon className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
                <div className="text-center">
                  <h4 className="text-lg font-semibold text-gray-900">
                    {selectedUser.name}
                  </h4>
                  <p className="text-sm text-gray-500">{selectedUser.user_number}</p>
                </div>
                <div className="border-t pt-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">전화번호:</span>
                      <p className="text-gray-600">
                        {userManagementService.formatPhoneNumber(selectedUser.phone)}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">이메일:</span>
                      <p className="text-gray-600">{selectedUser.email || '없음'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">상태:</span>
                      <p
                        className={`font-semibold ${
                          selectedUser.is_active ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {userManagementService.getUserStatusLabel(
                          selectedUser.is_active
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">가입일:</span>
                      <p className="text-gray-600">
                        {userManagementService.formatDate(selectedUser.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="font-medium text-gray-700">선호 좌석:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedUser.preferred_seats &&
                      selectedUser.preferred_seats.length > 0 ? (
                        selectedUser.preferred_seats.map((seat) => (
                          <span
                            key={seat}
                            className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                          >
                            {seat}번
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400">없음</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between mt-6">
                <button
                  onClick={() => {
                    closeUserModal();
                    openInviteModal(selectedUser);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 flex items-center gap-2"
                >
                  <UserGroupIcon className="h-4 w-4" />
                  부모님 초대
                </button>
                <button
                  onClick={closeUserModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 학부모 초대 모달 */}
      {showInviteModal && selectedStudent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 text-center flex items-center justify-center gap-2">
                <UserGroupIcon className="h-6 w-6 text-purple-600" />
                학부모 초대 링크 생성
              </h3>

              <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong className="text-purple-700">{selectedStudent.name}</strong> 학생의 학부모를 초대합니다
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  학부모가 초대 링크로 가입하면 자동으로 {selectedStudent.name} 학생과 연결됩니다
                </p>
              </div>

              {!inviteUrl ? (
                // Step 1: 전화번호 입력 및 초대 링크 생성
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      학부모 전화번호 *
                    </label>
                    <input
                      type="tel"
                      value={parentPhone}
                      onChange={(e) => setParentPhone(e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="010-1234-5678"
                      disabled={inviteLoading}
                    />
                  </div>

                  {inviteError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                      {inviteError}
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={closeInviteModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      disabled={inviteLoading}
                    >
                      취소
                    </button>
                    <button
                      onClick={handleCreateInvite}
                      disabled={!parentPhone.trim() || inviteLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {inviteLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          생성 중...
                        </>
                      ) : (
                        '초대 링크 생성'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                // Step 2: 생성된 초대 링크 표시
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      생성된 초대 링크
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={inviteUrl}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                      />
                      <button
                        onClick={handleCopyUrl}
                        className={`px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 ${
                          copied
                            ? 'bg-green-600 text-white'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {copied ? (
                          <>
                            <CheckIcon className="h-4 w-4" />
                            복사됨
                          </>
                        ) : (
                          <>
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            복사
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">
                      다음 단계
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>복사한 링크를 학부모에게 카카오톡/문자로 전송</li>
                      <li>학부모가 링크를 클릭하여 회원가입 진행</li>
                      <li>SMS 인증 후 자동으로 {selectedStudent.name} 학생과 연결됨</li>
                      <li>링크 유효기간: 7일</li>
                    </ul>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={closeInviteModal}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
                    >
                      확인
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        🧪 현재 테스트 데이터를 표시하고 있습니다. 실제 API 연동 후 실제 사용자
        데이터로 교체됩니다.
      </div>
    </div>
  );
};

export default UserManagement;
